import type { EChartsOption } from 'echarts';
import { useMemo } from 'react';

import { useTheme } from '../../theme/readTheme';
import type { ChartData, ChartFields } from '../../types';
import { EChart } from '../EChart';
import { Plot } from '../Plot';
import { Empty } from '../states';
import { cell, humanise, isNumeric } from '../format';
import { animation, tooltipChrome } from '../options';

/**
 * Density across two dimensions — the form for "where in this grid is the mass?".
 *
 * Expects two categorical columns (the axes) and one numeric column (the cell
 * value): `[row, column, value]`. Colour intensity is a single-hue ramp,
 * light-to-dark, never a rainbow — a heatmap's whole job is to let the eye read
 * magnitude off colour, and a multi-hue scale breaks that ordering.
 *
 * The ramp is the app's own sequential scale (`--seq-*`), so it themes with the
 * rest of the palette in both light and dark.
 */

interface Props {
  data: ChartData;
  /** Author's column choices; absent means infer. */
  fields?: ChartFields;
}

const heatmapView = (data: ChartData, theme: ReturnType<typeof useTheme>) => {
  if (data.columns.length < 3 || !data.rows.length) return null;

  const numericAt = (i: number) => data.rows.slice(0, 30).some((r) => isNumeric(r[i]));
  const dims = data.columns.map((_, i) => i).filter((i) => !numericAt(i));
  const measureIdx = data.columns.findIndex((_, i) => numericAt(i));
  if (dims.length < 2 || measureIdx < 0) return null;

  const [yi, xi] = dims; // first dimension down the side, second along the bottom
  const text = (v: unknown) => String(v ?? '');

  // First-seen order preserves the query's ORDER BY on both axes.
  const xs: string[] = [];
  const ys: string[] = [];
  for (const r of data.rows) {
    const xv = text(r[xi]);
    const yv = text(r[yi]);
    if (!xs.includes(xv)) xs.push(xv);
    if (!ys.includes(yv)) ys.push(yv);
  }

  const xIndex = new Map(xs.map((v, i) => [v, i]));
  const yIndex = new Map(ys.map((v, i) => [v, i]));

  let max = 0;
  const cells = data.rows
    .filter((r) => isNumeric(r[measureIdx]))
    .map((r) => {
      const v = r[measureIdx] as number;
      if (v > max) max = v;
      return [xIndex.get(text(r[xi])) ?? 0, yIndex.get(text(r[yi])) ?? 0, v] as [number, number, number];
    });

  if (!cells.length) return null;

  const xName = humanise(data.columns[xi]);
  const yName = humanise(data.columns[yi]);
  const measureName = humanise(data.columns[measureIdx]);
  const ramp = theme.sequential.length ? theme.sequential : [theme.brand];

  const axisCommon = {
    axisLine: { show: false },
    axisTick: { show: false },
    splitArea: { show: false },
    axisLabel: { color: theme.inkMuted, fontSize: 11, fontFamily: theme.font, hideOverlap: true },
  };

  const option: EChartsOption = {
    ...animation,
    grid: { left: 4, right: 12, top: 8, bottom: 48, containLabel: true },
    xAxis: {
      type: 'category' as const,
      data: xs,
      name: xName,
      ...axisCommon,
      axisLabel: { ...axisCommon.axisLabel, rotate: xs.length > 8 ? 35 : 0 },
    },
    yAxis: { type: 'category' as const, data: ys, name: yName, ...axisCommon },
    visualMap: {
      min: 0,
      max: max || 1,
      calculable: true,
      orient: 'horizontal' as const,
      left: 'center',
      bottom: 0,
      itemWidth: 12,
      itemHeight: 90,
      inRange: { color: ramp },
      textStyle: { color: theme.inkMuted, fontSize: 11, fontFamily: theme.font },
      formatter: (v: unknown) => cell(Number(v)),
    },
    tooltip: {
      trigger: 'item',
      ...tooltipChrome(theme),
      formatter: (params: unknown) => {
        const p = params as { data: [number, number, number] };
        const [x, y, v] = p.data;
        const root = document.createElement('div');
        root.className = 'chart-tip';
        const head = document.createElement('div');
        head.className = 'chart-tip__title';
        head.textContent = `${ys[y]} · ${xs[x]}`;
        const row = document.createElement('div');
        row.className = 'chart-tip__row';
        const n = document.createElement('span');
        n.className = 'chart-tip__name';
        n.textContent = measureName;
        const val = document.createElement('strong');
        val.className = 'chart-tip__value';
        val.textContent = cell(v, 'exact');
        row.append(n, val);
        root.append(head, row);
        return root;
      },
    },
    series: [
      {
        type: 'heatmap' as const,
        data: cells,
        itemStyle: { borderColor: theme.surface, borderWidth: 2 },
        emphasis: { itemStyle: { borderColor: theme.ink, borderWidth: 1 } },
      },
    ],
  };

  return { option };
};

export const Heatmap = ({ data }: Props) => {
  const theme = useTheme();
  const view = useMemo(() => heatmapView(data, theme), [data, theme]);
  if (!view) return <Empty message="Needs two category columns and a measure" />;
  return (
    <Plot>
      <EChart option={view.option} ariaLabel="Heatmap" />
    </Plot>
  );
};
